import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Paper,
    Typography,
    Alert,
    Divider,
    Stepper,
    Step,
    StepLabel,
    ToggleButtonGroup,
    ToggleButton,
    Link,
} from '@mui/material';
import {
    ArrowForward,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import Btn from '../global/Btn';
import FormInput from '../inputs/FormInput';
import OtpInput from './OtpInput';
import OtpCountdownTimer from './OtpCountdownTimer';
import OtpResendButton from './OtpResendButton';
import { useRegister } from '../../hooks/auth/useRegister';
import { useOtpResend } from '../../hooks/auth/useOtpResend';
import { useAuthContext } from '../../auth/useAuthContext';
import { maskEmail, maskPhoneNumber } from '../../utils/otpHelpers';
import { countries } from '../../constants/countres';

const FormOtpRegister = ({ onBack, initialData }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { completeRegisterWithOtp } = useAuthContext();

    const [step, setStep] = useState(initialData ? 2 : 1); // 1: Details, 2: Verification
    const [inputType, setInputType] = useState('phone');
    const [name, setName] = useState(initialData?.name || '');
    const [email, setEmail] = useState(initialData?.email || '');
    const [selectedCountryCode, setSelectedCountryCode] = useState(initialData?.selectedCountryCode || countries[0].phone);
    const [phoneNumber, setPhoneNumber] = useState(initialData?.phoneNumber || '');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [lang, setLang] = useState('ar');
    const [otp, setOtp] = useState('');

    const phone = phoneNumber ? `+${selectedCountryCode}${phoneNumber}` : '';

    const registerHook = useRegister();
    const otpResend = useOtpResend();

    useEffect(() => {
        if (initialData && !registerHook.registrationData) {
            registerHook.setRegistrationData(initialData);
        }
    }, [initialData, registerHook]);

    // Manual state handling is used for inputs

    const handleInitiateRegister = (e) => {
        if (e && e.preventDefault) e.preventDefault();
        if (inputType === 'phone' && !phoneNumber) {
            toast.error(t('validation.phoneRequired'));
            return;
        }
        if (inputType === 'email' && !email) {
            toast.error(t('validation.emailRequired'));
            return;
        }
        if (inputType === 'both' && (!phoneNumber || !email)) {
            toast.error(t('validation.bothRequired'));
            return;
        }

        registerHook.initiate(
            {
                name,
                phone: phoneNumber ? phone : undefined,
                email: email || undefined,
                password,
                password_confirmation: confirmPassword,
                lang
            },
            {
                onSuccess: (data) => {
                    toast.success(data?.message || t('otp.code_sent_success'));
                    setStep(2);
                    setOtp('');
                },
                onError: (error) => {
                    const message = error?.response?.data?.message || t('otp.send_failed');
                    toast.error(message);
                    const errors = error?.response?.data?.errors;
                    if (errors) {
                        Object.values(errors).flat().forEach(err => toast.error(err));
                    }
                }
            }
        );
    };

    const handleVerifyOtp = (otpCode) => {
        const cart_session_id = localStorage.getItem('cart_session_id');

        registerHook.verify(
            { otp: otpCode, cart_session_id },
            {
                onSuccess: async (data) => {
                    if (data?.success && data?.data) {
                        if (completeRegisterWithOtp) {
                            await completeRegisterWithOtp(data.data);
                        }
                        toast.success(data?.message || t('otp.registration_success'));
                        navigate('/');
                    }
                },
                onError: (error) => {
                    const message = error?.response?.data?.message || t('otp.invalid_code');
                    toast.error(message);
                }
            }
        );
    };

    const handleResend = () => {
        const regData = registerHook.registrationData || initialData;
        if (!regData) return;
        const { channel, identifier, purpose } = regData;

        otpResend.mutate(
            { channel, identifier, purpose },
            {
                onSuccess: (data) => {
                    toast.success(data?.message || t('otp.code_resent'));
                    setOtp('');
                },
                onError: (error) => {
                    const message = error?.response?.data?.message || t('otp.resend_failed');
                    toast.error(message);
                }
            }
        );
    };

    const handleBack = () => {
        setStep(1);
        setOtp('');
        registerHook.clearRegistrationData();
    };

    const handleExpire = useCallback(() => {
        // Only show expired toast if we are not currently resending
        if (!otpResend.isPending) {
            toast.error(t('otp.code_expired'));
        }
    }, [otpResend.isPending, t]);

    const steps = [t('otp.details'), t('otp.verification')];

    return (
        <Paper
            elevation={4}
            sx={{
                px: { md: 4, xs: 2 },
                py: { md: 4, xs: 3 },
                borderRadius: 3,
                maxWidth: 500,
                mx: 'auto',
                bgcolor: 'background.paper',
            }}
        >
            <Typography variant="h5" textAlign="center" sx={{ mb: 3, fontWeight: 700 }}>
                {t('otp.registration')}
            </Typography>

            <Stepper activeStep={step - 1} sx={{ mb: 4 }}>
                {steps.map((label) => (
                    <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                    </Step>
                ))}
            </Stepper>

            {step === 1 ? (
                <Box component="form" onSubmit={handleInitiateRegister}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <FormInput
                            label={t('contact.name')}
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder={t('contact.name')}
                            required
                        />

                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                {t('otp.contact_through')}
                            </Typography>
                            <ToggleButtonGroup
                                value={inputType}
                                exclusive
                                onChange={(e, v) => v && setInputType(v)}
                                fullWidth
                                size="small"
                                sx={{ mb: 1 }}
                            >
                                <ToggleButton value="phone">{t('otp.phone')}</ToggleButton>
                                <ToggleButton value="email">{t('otp.email')}</ToggleButton>
                                <ToggleButton value="both">{t('otp.both')}</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>

                        {(inputType === 'phone' || inputType === 'both') && (
                            <FormInput
                                type="tel"
                                label={t('auth.phone')}
                                placeholder="555-555-555"
                                selectedCountryCode={selectedCountryCode}
                                setSelectedCountryCode={setSelectedCountryCode}
                                phoneNumber={phoneNumber}
                                setPhoneNumber={setPhoneNumber}
                                disabled={registerHook.isInitiating}
                            />
                        )}

                        {(inputType === 'email' || inputType === 'both') && (
                            <FormInput
                                label={t('auth.email')}
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="example@example.com"
                                disabled={registerHook.isInitiating}
                            />
                        )}

                        <FormInput
                            label={t('auth.password')}
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="********"
                            required
                            helperText={t('otp.pwd_helper')}
                        />

                        <FormInput
                            label={t('auth.confirmPassword')}
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder="********"
                            required
                        />

                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                {t('profile.preferredLanguage')}
                            </Typography>
                            <FormInput
                                type="select"
                                value={lang}
                                onChange={(e) => setLang(e.target.value)}
                                options={[
                                    { id: 'ar', name: t('languages.arabic') },
                                    { id: 'en', name: t('languages.english') },
                                ]}
                            />
                        </Box>
                    </Box>

                    <Box mt={4} textAlign="center">
                        <Btn
                            type="submit"
                            width="100%"
                            widthMob="100%"
                            sx={{ mx: 'auto' }}
                            loading={registerHook.isInitiating}
                            endIcon={<ArrowForward />}
                        >
                            {t('common.next')}
                        </Btn>
                    </Box>
                </Box>
            ) : (
                <Box>
                    {(registerHook.registrationData || initialData) && (
                        <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                            {t('otp.code_sent_to')}:<br />
                            <strong>
                                {(registerHook.registrationData?.channel || initialData?.channel) === 'sms'
                                    ? maskPhoneNumber(registerHook.registrationData?.identifier || initialData?.identifier)
                                    : maskEmail(registerHook.registrationData?.identifier || initialData?.identifier)
                                }
                            </strong>
                        </Alert>
                    )}

                    <Box sx={{ mb: 4 }}>
                        <Typography variant="subtitle2" mb={1} textAlign="center">
                            {t('otp.verification')}
                        </Typography>
                        <OtpInput
                            value={otp}
                            onChange={setOtp}
                            onComplete={handleVerifyOtp}
                            error={!!registerHook.verifyError}
                            disabled={registerHook.isVerifying}
                        />
                    </Box>

                    {(registerHook.registrationData?.expires_at || initialData?.expires_at) && (
                        <Box sx={{ mb: 3 }}>
                            <OtpCountdownTimer
                                expiresAt={registerHook.registrationData?.expires_at || initialData?.expires_at}
                                onExpire={handleExpire}
                            />
                        </Box>
                    )}

                    {registerHook.attemptsRemaining !== null && (
                        <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
                            {t('otp.attempts_remaining')}: {registerHook.attemptsRemaining}
                        </Alert>
                    )}

                    <Btn
                        type="button"
                        onClick={() => handleVerifyOtp(otp)}
                        width="100%"
                        widthMob="100%"
                        loading={registerHook.isVerifying}
                        disabled={!registerHook.isVerifying && otp.length !== 6}
                        sx={{ mb: 2, mx: 'auto' }}
                    >
                        {t('otp.create_account')}
                    </Btn>

                    <Divider sx={{ my: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                            {t('auth.orContinueWith')}
                        </Typography>
                    </Divider>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <OtpResendButton
                            onResend={handleResend}
                            cooldownSeconds={registerHook.registrationData?.cooldown_seconds || initialData?.cooldown_seconds}
                            loading={otpResend.isPending}
                        />

                        <Btn
                            type="button"
                            onClick={handleBack}
                            variant="text"
                            width="100%"
                            widthMob="100%"
                        >
                            {step === 2 ? t('otp.change_details') : t('common.previous')}
                        </Btn>
                    </Box>
                </Box>
            )}

            <Box sx={{ textAlign: 'center', mt: 3 }}>
                <Typography variant="body2">
                    {t('auth.alreadyHaveAccount')}{' '}
                    <Link
                        component={RouterLink}
                        to="/login"
                        sx={{ fontWeight: 600, textDecoration: 'none' }}
                    >
                        {t('auth.signIn')}
                    </Link>
                </Typography>
            </Box>
        </Paper>
    );
};

export default FormOtpRegister;
